import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { OrderService } from 'src/app/order.service';
import { Store } from 'src/app/models/store';
import { Subject, forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as moment from 'moment';
import { environment } from 'src/environments/environment';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';


@Component({
  selector: 'app-offer-detail',
  templateUrl: './offer-detail.component.html',
  styleUrls: ['./offer-detail.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OfferDetailComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();
  navigationSubscription;
  customPrice: any;

  constructor(private route: ActivatedRoute,
    private router: Router,
    private orderService: OrderService,
    private formBuilder: FormBuilder,
    private cdr: ChangeDetectorRef) {
  }

  storeId: string;
  offerId: string;
  offerOrderId: string;
  store: Store;
  offers: any[];
  selectedOffer: any;
  isLoading = true;
  ends;
  offerCatalogSharableLink;
  getOfferType: any;
  form: FormGroup
  cardType: any;

  presetAmounts = [25, 50, 100];
  selectedPreset: number | 'custom' = null;

  purchaseType: 'self' | 'gift' = 'self';
  giftForm: FormGroup;
  giftFormSubmitted = false;
  sendTiming: 'now' | 'later' = 'now';

  ngOnInit() {

    this.form = this.formBuilder.group({
      price_value: ''
    })

    this.giftForm = this.formBuilder.group({
      recipient_name: ['', Validators.required],
      delivery_method: ['SMS', Validators.required],
      recipient_phone: [''],
      recipient_email: [''],
      sender_name: ['', Validators.required],
      message: ['', Validators.maxLength(200)],
      scheduled_date: [''],
      scheduled_time: ['']
    });

    this.storeId = this.route.snapshot.paramMap.get('storeId');
    this.offerOrderId = this.route.snapshot.paramMap.get('offerOrderId');
    this.offerId = this.route.snapshot.paramMap.get('offerId');



    if (!this.offerOrderId) {
      this.orderService.createTempOfferOrder(this.storeId).subscribe(data => {
        this.offerOrderId = data['offer_order_id']
        this.router.navigate(['store', this.storeId, 'offers', 'order', this.offerOrderId, 'offer', this.offerId]);
        this.cdr.markForCheck();
      })
    } else {
      this.restoreGiftDetails();
      forkJoin({
        store: this.orderService.getStoreInfo(this.storeId),
        offer: this.orderService.getOfferDetails(this.storeId, this.offerOrderId, this.offerId)
      }).pipe(takeUntil(this.destroy$)).subscribe(({ store, offer }: any) => {
        this.store = Object.assign(new Store(), store);
        this.offerCatalogSharableLink = `${environment.hostName}/store/${this.store.url_slug}/offers/${this.offerId}`;
        this.selectedOffer = offer;
        this.getOfferType = this.selectedOffer.offer_type;
        this.ends = moment(this.selectedOffer.ends).valueOf();
        this.isLoading = false;
        this.cdr.markForCheck();
      });
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get giftDetailsKey() {
    return `giftDetails_${this.offerOrderId}`;
  }

  private restoreGiftDetails() {
    const saved = localStorage.getItem(this.giftDetailsKey);
    if (saved) {
      try {
        const details = JSON.parse(saved);
        this.purchaseType = 'gift';
        if (details.scheduled_at) {
          this.sendTiming = 'later';
          const [datePart, timePart] = details.scheduled_at.split('T');
          details.scheduled_date = datePart;
          details.scheduled_time = timePart ? timePart.substring(0, 5) : '';
        }
        this.giftForm.patchValue(details);
      } catch (e) {
        localStorage.removeItem(this.giftDetailsKey);
      }
    }
  }

  setPurchaseType(type: 'self' | 'gift') {
    this.purchaseType = type;
    if (type === 'self') {
      localStorage.removeItem(this.giftDetailsKey);
    }
  }

  setDeliveryMethod(method: 'SMS' | 'EMAIL') {
    this.giftForm.patchValue({ delivery_method: method });
  }

  get deliveryMethod() {
    return this.giftForm.value.delivery_method;
  }

  selectPreset(amount: number | 'custom') {
    this.selectedPreset = amount;
    if (amount === 'custom') {
      this.form.patchValue({ price_value: '' });
    } else {
      this.form.patchValue({ price_value: amount });
    }
  }

  onCustomValueInput() {
    this.selectedPreset = 'custom';
  }

  private plainPhone(phoneNumber: string) {
    let digits = (phoneNumber || '').replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) {
      digits = digits.substring(1);
    }
    return digits.length >= 10 ? '+1' + digits.substring(0, 10) : '';
  }

  get phoneIncomplete(): boolean {
    const value = this.giftForm.value.recipient_phone;
    return !!value && this.plainPhone(value) === '';
  }

  private isEmailValid(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || '').trim());
  }

  get emailIncomplete(): boolean {
    const value = this.giftForm.value.recipient_email;
    return !!value && !this.isEmailValid(value);
  }

  get minDate(): string {
    return new Date().toISOString().split('T')[0];
  }

  isGiftFormValid(): boolean {
    const v = this.giftForm.value;
    if (!v.recipient_name || !v.sender_name) return false;
    if (v.delivery_method === 'EMAIL') {
      if (!this.isEmailValid(v.recipient_email)) return false;
    } else {
      if (this.plainPhone(v.recipient_phone).length !== 12) return false;
    }
    if (this.sendTiming === 'later' && (!v.scheduled_date || !v.scheduled_time)) return false;
    return true;
  }

  isAmountValid(): boolean {
    if (this.getOfferType !== 'CUSTOM') return true;
    const value = parseFloat(this.form.value.price_value);
    return !isNaN(value) && value > 0;
  }

  canContinue(): boolean {
    if (!this.selectedOffer?.is_valid) return false;
    if (!this.isAmountValid()) return false;
    if (this.purchaseType === 'gift') return this.isGiftFormValid();
    return true;
  }

  claimNow() {
    this.giftFormSubmitted = true;
    if (!this.canContinue()) return;

    if (this.purchaseType === 'gift') {
      const v = this.giftForm.value;
      const details: any = {
        recipient_name: (v.recipient_name || '').trim(),
        sender_name: (v.sender_name || '').trim(),
        message: (v.message || '').trim(),
        delivery_method: v.delivery_method,
        recipient_phone: v.delivery_method === 'SMS' ? this.plainPhone(v.recipient_phone) : '',
        recipient_email: v.delivery_method === 'EMAIL' ? (v.recipient_email || '').trim() : ''
      };
      if (this.sendTiming === 'later' && v.scheduled_date && v.scheduled_time) {
        details.scheduled_at = new Date(`${v.scheduled_date}T${v.scheduled_time}`).toISOString();
      }
      localStorage.setItem(this.giftDetailsKey, JSON.stringify(details));
    } else {
      localStorage.removeItem(this.giftDetailsKey);
    }

    this.customPrice = this.form.value.price_value;
    if(this.customPrice != 0) {
      this.cardType = 1;
    } else {
      this.cardType = 0;
      this.customPrice = 0;
    }
    this.orderService.addOffer(this.storeId, this.offerOrderId, this.offerId, this.customPrice, this.cardType).subscribe(store => {
      this.router.navigate(['store', this.storeId, 'offers', 'order', this.offerOrderId])
    })
  }

  goBack() {
    this.router.navigate(['store', this.storeId, 'offers', 'order', this.offerOrderId, 'available'])
  }

}
